import { useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { generateGoogleOAuthUrl, GOOGLE_OAUTH_ENDPOINTS, validateGoogleOAuthConfig } from '../config/googleOAuth';
import { getUserTimezone } from '../utils/timezoneUtils';

interface GoogleUserInfo {
  id: string;
  email: string;
  name: string;
  picture: string;
  given_name: string;
  family_name: string;
}

export const useGoogleOAuth = () => {
  const { socialLogin } = useAuth();
  const userTypeRef = useRef<number>(1);
  const callbacksRef = useRef<{
    onSuccess?: (_message?: string) => void;
    onError?: (_error: string) => void;
  }>({});
  // Cleanup for the in-progress OAuth flow (clear interval, remove listeners,
  // close popup). Set from inside `googleLogin`, called by `cancelOAuth`.
  const cleanupRef = useRef<(() => void) | null>(null);

  const handleGoogleSuccess = useCallback(async (credentialResponse: any) => {
    try {
      // Get user info from Google using config endpoint
      const response = await fetch(GOOGLE_OAUTH_ENDPOINTS.userInfo, {
        headers: {
          Authorization: `Bearer ${credentialResponse.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user info from Google');
      }

      const userInfo: GoogleUserInfo = await response.json();

      // Prepare data for social login
      const socialData = {
        loginType: 'google' as const,
        accessToken: credentialResponse.access_token,
        email: userInfo.email,
        name: userInfo.name,
        picture: userInfo.picture,
        firstName: userInfo.given_name,
        lastName: userInfo.family_name,
        userType: userTypeRef.current, // Use stored userType
        timezone: getUserTimezone(),
      };

      // Call the social login function
      const result = await socialLogin(socialData);

      // Show success message based on backend response
      if (result.success) {
        // Pass success message from backend if available
        const successMessage = (result as any).message || 'Account created successfully. Redirecting to dashboard...';
        callbacksRef.current.onSuccess?.(successMessage);
      } else {
        console.error('Google login failed:', result.message);
        // Pass the actual backend error message to the modal
        callbacksRef.current.onError?.(result.error || 'Google login failed');
      }

      return result;
    } catch (error) {
      console.error('Google OAuth error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Google login failed';
      callbacksRef.current.onError?.(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [socialLogin]);

  // const googleLogin = useGoogleLogin({
  //   onSuccess: (credentialResponse) => handleGoogleSuccess(credentialResponse),
  //   onError: (error) => {
  //     console.error('Google login error:', error);
  //     callbacksRef.current.onError?.('Google login cancelled');
  //   },
  //   scope: 'email profile',
  // });

  // Google OAuth login function using popup
  const googleLogin = useCallback(() => {
    if (!validateGoogleOAuthConfig()) {
      throw new Error('Google OAuth is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file.');
    }

    // Generate Google OAuth URL for popup flow using config method
    // const authUrl = generateMicrosoftOAuthPopupUrl();
    const authUrl = generateGoogleOAuthUrl();
    console.log('authUrl', authUrl);

    // On iOS inside a React Native WebView, window.open() is blocked by WKWebView
    // and Google rejects the flow with disallowed_useragent. Delegate to the native
    // layer instead; it will post OAUTH_SUCCESS / OAUTH_ERROR back via injectJavaScript.
    const isReactNativeWebView = typeof window !== 'undefined' && !!window.ReactNativeWebView;
    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/.test(ua);

    if (isReactNativeWebView && isIOS) {
      window.ReactNativeWebView!.postMessage(JSON.stringify({ type: 'GOOGLE_AUTH_REQUEST' }));
      // The existing window + document message listeners below handle OAUTH_SUCCESS / OAUTH_ERROR.
      // Register them now before returning so the native response is caught.
      const handleNativeMessage = (event: MessageEvent) => {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.type === 'OAUTH_SUCCESS') {
          window.removeEventListener('message', handleNativeMessage);
          document.removeEventListener('message', handleNativeMessage as any);
          handleGoogleSuccess({ access_token: data.access_token });
        } else if (data.type === 'OAUTH_ERROR') {
          window.removeEventListener('message', handleNativeMessage);
          document.removeEventListener('message', handleNativeMessage as any);
          callbacksRef.current.onError?.(data.error || 'Google login failed');
        }
      };
      window.addEventListener('message', handleNativeMessage);
      // @ts-ignore - RN dispatches custom event on document too
      document.addEventListener('message', handleNativeMessage);
      cleanupRef.current = () => {
        window.removeEventListener('message', handleNativeMessage);
        // @ts-ignore
        document.removeEventListener('message', handleNativeMessage);
        cleanupRef.current = null;
      };
      return;
    }

    // Open popup window
    const popup = window.open(authUrl, 'google-login', 'width=500,height=600,scrollbars=yes,resizable=yes');

    if (!popup) {
      throw new Error('Popup blocked. Please allow popups for this site.');
    }
    let settled = false;

    const cleanup = () => {
      settled = true;
      if (checkClosed) clearInterval(checkClosed);
      try { popup.close(); } catch {console.log("Error while closing popup")}
      window.removeEventListener('message', handleMessage);
      // @ts-ignore - RN dispatches custom event
      document.removeEventListener('message', handleMessage);
      cleanupRef.current = null;
    };

    const checkClosed = isReactNativeWebView
      ? null
      : setInterval(() => {
          if (popup.closed) {
            if (settled) {
              clearInterval(checkClosed as any);
              return;
            }
            cleanup();
            callbacksRef.current.onError?.('Login was cancelled');
          }
        }, 1000);

    // Listen for messages from popup. Once `settled` is true (either because a
    // success/error arrived or the user cancelled via cancelOAuth), late
    // messages are ignored.
    const handleMessage = (event: MessageEvent) => {
      if (settled) return;
      if (event.origin && event.origin !== window.location.origin) return;
      const data = typeof event.data == "string" ? JSON.parse(event.data) : event.data;
      if (data.type === 'OAUTH_SUCCESS') {
        cleanup();
        const { access_token } = data;
        handleGoogleSuccess({ access_token });
      } else if (data.type === 'OAUTH_ERROR') {
        cleanup();
        callbacksRef.current.onError?.(data.error || 'Google login failed');
      }
    };

    window.addEventListener('message', handleMessage);
    // @ts-ignore - RN dispatches custom event
    document.addEventListener('message', handleMessage);

    // Expose cleanup so the hook consumer can cancel from an overlay button
    cleanupRef.current = cleanup;

  }, [handleGoogleSuccess]);

  // Called by the UI (e.g. overlay Cancel button) to abort an in-progress flow.
  // Late OAUTH_SUCCESS / OAUTH_ERROR messages arriving after this are ignored.
  const cancelOAuth = useCallback(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
    }
  }, []);


  // Create a function that sets userType and returns the login function
  const googleLoginWithUserType = useCallback((userType: number, onSuccess?: (_message?: string) => void, onError?: (_error: string) => void) => {
    userTypeRef.current = userType;
    // Store the callbacks for later use
    callbacksRef.current = { onSuccess, onError };
    // Return a function that can be called to trigger the login
    return () => {
      googleLogin();
    };
  }, [googleLogin]);

  return {
    googleLogin,
    googleLoginWithUserType,
    handleGoogleSuccess,
    cancelOAuth,
  };
};